import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function Analytics() {
  const [overview, setOverview] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setOverview(r.data));
    api.get("/analytics/sessions").then((r) => setSessions(r.data));
  }, []);

  return (
    <>
      <PageHeader
        eyebrow="Insights"
        title="Analytics"
        description="How customers are engaging with your experience."
      />

      <div className="p-10 space-y-10">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="bg-white border border-[#e5e5e5] p-6 lg:col-span-2">
            <div className="label-overline">Top recommendations</div>
            <div className="mt-2 font-display font-bold text-xl tracking-tight">Most-shown products</div>
            <div className="h-64 mt-6">
              {overview?.top_products?.length ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overview.top_products}>
                    <CartesianGrid stroke="#e5e5e5" strokeDasharray="0" vertical={false} />
                    <XAxis dataKey="title" stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#52525b" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 0, border: "1px solid #e5e5e5", fontFamily: "IBM Plex Sans" }} />
                    <Bar dataKey="count" fill="#0F1210" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-full flex items-center justify-center text-sm text-[#52525b] border border-dashed border-[#e5e5e5]">
                  No data yet.
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-[#e5e5e5] p-6">
            <div className="label-overline">North-star</div>
            <div className="mt-2 font-display font-bold text-xl tracking-tight">Customer sessions</div>
            <div className="mt-8 font-display font-black tracking-tighter text-7xl">
              {overview?.total_sessions ?? 0}
            </div>
            <div className="mt-4 text-xs text-[#52525b]">Total completed experiences.</div>
          </div>
        </div>

        <div className="bg-white border border-[#e5e5e5]">
          <div className="px-6 py-4 label-overline border-b border-[#e5e5e5]">Recent sessions</div>
          {sessions.length === 0 && (
            <div className="px-6 py-12 text-sm text-[#52525b] text-center">No sessions recorded yet.</div>
          )}
          <ul>
            {sessions.map((s) => (
              <li
                key={s.id}
                className="px-6 py-4 border-b border-[#e5e5e5] last:border-b-0 grid grid-cols-12 gap-4 items-start"
                data-testid={`session-row-${s.id}`}
              >
                <div className="col-span-3">
                  <div className="font-mono text-xs text-[#52525b]">{s.created_at?.slice(0, 16).replace("T", " ")}</div>
                  <div className="mt-1 text-xs">Score: <span className="font-mono">{s.match_score}</span></div>
                </div>
                <div className="col-span-6">
                  <div className="text-sm font-medium">{s.reason}</div>
                  <div className="mt-1 text-xs text-[#52525b]">
                    {s.answers?.map((a, i) => (
                      <span key={i} className="inline-block mr-3">
                        Q{i + 1}: {(a.selected || []).map((x) => (typeof x === "string" ? x : x.label)).join(", ")}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="col-span-3 text-right">
                  <span className="label-overline">Token</span>
                  <div className="mt-1 font-mono text-xs truncate">{s.experience_token}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </>
  );
}
